import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { StepProps, RepairEstimateRange, REPAIR_ESTIMATE_LABELS } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const SectionLabel = styled.div`
  ${tw`text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-6`}
`;

const OptionsContainer = styled.div`
  ${tw`flex flex-col gap-3 mb-8`}
`;

const OptionButton = styled.button<{ isSelected: boolean }>`
  ${tw`w-full p-4 text-left border-2 rounded-card transition-all flex items-center justify-between`}
  ${props =>
    props.isSelected
      ? tw`border-black bg-pink-50`
      : tw`border-border-default hover:border-border-default`}
`;

const OptionText = styled.span`
  ${tw`font-medium text-text-primary`}
`;

const CheckIcon = styled.div<{ isSelected: boolean }>`
  ${tw`w-6 h-6 rounded-full flex items-center justify-center`}
  ${props => (props.isSelected ? tw`bg-black` : tw`bg-bg-secondary`)}
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

const estimateOptions = [
  RepairEstimateRange.BELOW_500,
  RepairEstimateRange.RANGE_500_1000,
  RepairEstimateRange.RANGE_1000_2000,
  RepairEstimateRange.OVER_2000,
  RepairEstimateRange.UNKNOWN,
];

const RepairEstimateStep: React.FC<StepProps> = ({
  formData,
  updateFormData,
  onNext,
  isSubmitting,
}) => {
  const handleSelect = (estimate: RepairEstimateRange) => {
    updateFormData({ repairEstimateRange: estimate });
  };

  const canSubmit = !!formData.repairEstimateRange;

  return (
    <Container>
      <SectionLabel>RESOLUTION PATH</SectionLabel>
      <Title>Damage resolution calculator</Title>
      <Subtitle>
        Select an estimated repair cost to help us determine the best resolution path.
      </Subtitle>

      <OptionsContainer>
        {estimateOptions.map(estimate => (
          <OptionButton
            key={estimate}
            isSelected={formData.repairEstimateRange === estimate}
            onClick={() => handleSelect(estimate)}
          >
            <OptionText>{REPAIR_ESTIMATE_LABELS[estimate]}</OptionText>
            <CheckIcon isSelected={formData.repairEstimateRange === estimate}>
              {formData.repairEstimateRange === estimate && (
                <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
              )}
            </CheckIcon>
          </OptionButton>
        ))}
      </OptionsContainer>

      <SubmitButton disabled={!canSubmit || !!isSubmitting} onClick={onNext}>
        {isSubmitting ? 'Submitting...' : 'Submit claim'}
      </SubmitButton>
    </Container>
  );
};

export default RepairEstimateStep;
